// ─────────────────────────────────────────────────────────────────────────────
// HeyWaitr+ pure helpers
//
// These mirror the logic in the mobile app (hey_waitr) so the web deals feed
// renders identical results. Kept side-effect free and dependency free so they
// are easy to test and reuse.
// ─────────────────────────────────────────────────────────────────────────────

// Canonical full day-name order as returned by GET /menu/discountedbyday.
export const CANONICAL_DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

// Index (Date.getDay) → full day name. getDay(): 0 = Sunday.
const DAY_INDEX_TO_FULL = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

// Convert "HH:MM" to minutes since midnight.
export const timeToMinutes = (time) => {
  if (typeof time !== 'string') return 0;
  const [hours, minutes] = time.split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};

// Round an end time up to the next hour when the minutes read ":59" — matches
// the way the backend stores end times (e.g. "14:59" → "15:00", "23:59" → "24:00").
export const formatEndTime = (time) => {
  if (typeof time !== 'string') return time;
  const [hours, minutes] = time.split(':').map(Number);
  if (minutes === 59) {
    if (hours === 23) return '24:00';
    const nextHour = (hours || 0) + 1;
    return `${nextHour.toString().padStart(2, '0')}:00`;
  }
  return time;
};

// True when the current wall-clock time falls inside the deal window. Handles
// windows that wrap past midnight (end < start).
export const isDealActiveNow = (startTime, endTime) => {
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const start = timeToMinutes(startTime);
  const end = timeToMinutes(endTime);
  if (end < start) {
    return nowMinutes >= start || nowMinutes <= end;
  }
  return nowMinutes >= start && nowMinutes <= end;
};

// Parse a "25%" / "-Infinity" style discount string into a number (0 when none).
export const parseDiscount = (discountPercent) => {
  if (typeof discountPercent !== 'string') {
    return Number.isFinite(discountPercent) ? discountPercent : 0;
  }
  const n = parseFloat(discountPercent.replace('%', '').replace('-Infinity', '0'));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

// Discount tier colour (matches mobile + website branding).
export const getTierColor = (discount) => {
  if (discount >= 40) return '#8aa81e';
  if (discount >= 30) return '#b8841a';
  if (discount >= 20) return '#6a6a60';
  return '#6b4ea0';
};

// "Today" for the deals feed. Anything before 05:00 still counts as the
// previous day, because deal windows run until ~05:00.
export const getTodayFull = () => {
  const now = new Date();
  const effectiveDate = now.getHours() < 5 ? new Date(now.getTime() - 24 * 60 * 60 * 1000) : now;
  return DAY_INDEX_TO_FULL[effectiveDate.getDay()];
};

// Days that actually have deals, ordered with today first then canonical order.
export const orderDaysWithTodayFirst = (availableDays, todayFull) => {
  const present = CANONICAL_DAYS.filter((d) => availableDays.includes(d));
  const todayIdx = present.indexOf(todayFull);
  if (todayIdx === -1) return present;
  return [...present.slice(todayIdx), ...present.slice(0, todayIdx)];
};

// Display suffix for a restaurant's prices. Falls back
// to "DKK" so older backend payloads (without Currency) render exactly as before.
export const getCurrencySuffix = (restaurant) =>
  restaurant?.Currency?.symbol_native || restaurant?.Currency?.code || 'DKK';

// Flatten a restaurant list into individual deals for the "Deals" (tilbud) view.
// Sorted live-first, then by discount descending — same as mobile.
export const flattenDeals = (restaurants, isToday) => {
  const deals = [];
  (restaurants || []).forEach((r) => {
    const currency = getCurrencySuffix(r);
    (r.Menu || []).forEach((m) => {
      const discount = parseDiscount(m.DiscountPercent);
      deals.push({
        key: `${r._id}-${m._id}`,
        restaurantId: r._id,
        restaurantName: r.Name,
        menuName: m.Name,
        menuImage: m.Image || undefined,
        discount,
        price: m.Price,
        oldPrice: m.OldPrice,
        currency,
        startTime: m.StartTime,
        endTime: m.EndTime,
        isLive: !!isToday && discount > 0 && isDealActiveNow(m.StartTime, m.EndTime),
      });
    });
  });
  deals.sort((a, b) => {
    if (a.isLive !== b.isLive) return a.isLive ? -1 : 1;
    return b.discount - a.discount;
  });
  return deals;
};

// Summarise a restaurant's discounted menu for the "Places" (steder) view.
export const summarizeRestaurantDeals = (restaurant, isToday) => {
  const discountedMenus = (restaurant.Menu || []).filter(
    (m) => parseDiscount(m.DiscountPercent) > 0,
  );
  const maxDiscount = discountedMenus.reduce(
    (max, m) => Math.max(max, parseDiscount(m.DiscountPercent)),
    0,
  );
  let timeRange;
  if (discountedMenus.length > 0) {
    const starts = discountedMenus.map((m) => m.StartTime).sort();
    const ends = discountedMenus.map((m) => m.EndTime).sort();
    timeRange = `${starts[0]}–${formatEndTime(ends[ends.length - 1])}`;
  }
  const hasActiveDeal =
    !!isToday && discountedMenus.some((m) => isDealActiveNow(m.StartTime, m.EndTime));
  return { maxDiscount, timeRange, hasActiveDeal, dealCount: discountedMenus.length };
};

// Total number of individual discounted deals across every day (for the hero badge).
export const countTotalDeals = (discountedByDay) => {
  if (!discountedByDay) return 0;
  let count = 0;
  Object.values(discountedByDay).forEach((restaurants) => {
    (restaurants || []).forEach((r) => {
      (r.Menu || []).forEach((m) => {
        if (parseDiscount(m.DiscountPercent) > 0) count += 1;
      });
    });
  });
  return count;
};

// True when the subscription object from the backend represents an active member.
export const isSubscriptionActive = (subscription) =>
  !!subscription && subscription.state === 'active';

// Sync the cached profile's HeyWaitr+ flag to a known value (e.g. the live result
// of /subscription/fetch), since the login flag can drift across devices. Returns
// the merged profile only when the flag changed, else null (nothing cached or
// already matches) — so callers can skip a needless context update.
export const syncCachedSubscribed = (isSubscribed) => {
  try {
    const stored = JSON.parse(localStorage.getItem('user_profile'));
    if (!stored) return null;
    if (!!stored.profile?.isSubscribed === !!isSubscribed) return null;
    const merged = {
      ...stored,
      profile: { ...(stored.profile || {}), isSubscribed: !!isSubscribed },
    };
    localStorage.setItem('user_profile', JSON.stringify(merged));
    return merged;
  } catch (e) {
    return null;
  }
};

// Optimistically flag the cached profile as a HeyWaitr+ member — used right after
// a successful subscribe so the navbar badge / gating reflect it without a
// re-login. Returns the merged profile, or null if there's nothing cached.
export const markCachedSubscribed = () => syncCachedSubscribed(true);

// Map the app's language codes to a BCP-47 locale for Intl formatting.
const LANG_TO_LOCALE = {
  da: 'da',
  no: 'nb',
  en: 'en',
  de: 'de',
  sv: 'sv',
  es: 'es',
};

// Localised full weekday name (e.g. "Monday" → "Mandag" for da). Falls back to
// the English name so the feed never renders blank.
export const getLocalizedDayName = (fullDay, language) => {
  const idx = CANONICAL_DAYS.indexOf(fullDay);
  if (idx === -1) return fullDay;
  // 2024-01-01 is a Monday; offset by the weekday index.
  const d = new Date(2024, 0, 1 + idx);
  try {
    const label = d.toLocaleDateString(LANG_TO_LOCALE[language] || 'en', {
      weekday: 'long',
    });
    return label.charAt(0).toUpperCase() + label.slice(1);
  } catch (e) {
    return fullDay;
  }
};
